import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Music, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", instrument: "", teacher_id: "none", monthly_fee: "", meetings_per_month: "4", description: "" };

export default function Classes() {
  const { user } = useAuth();
  const canManage = user?.role === "administrator";
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/classes").then((res) => setClasses(res.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/teachers").then((res) => setTeachers(res.data.filter((t) => t.status === "Aktif"))).catch(() => {});
  }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, instrument: c.instrument, teacher_id: c.teacher_id || "none",
      monthly_fee: String(c.monthly_fee), meetings_per_month: String(c.meetings_per_month || 4), description: c.description || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, teacher_id: form.teacher_id === "none" ? null : form.teacher_id,
      monthly_fee: Number(form.monthly_fee) || 0, meetings_per_month: Number(form.meetings_per_month) || 4 };
    try {
      if (editing) {
        await api.put(`/classes/${editing.id}`, payload);
        toast.success("Kelas diperbarui");
      } else {
        await api.post("/classes", payload);
        toast.success("Kelas baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan kelas"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kelas "${c.name}"? Siswa di kelas ini tidak ikut terhapus.`)) return;
    try {
      await api.delete(`/classes/${c.id}`);
      toast.success("Kelas dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus kelas"));
    }
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Kelas & Instrumen</h1>
          <p className="mt-1 text-sm text-muted-foreground">{classes.length} kelas tersedia.</p>
        </div>
        {canManage && (
          <Button onClick={openAdd} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-class-button">
            <Plus className="h-4 w-4" /> Tambah Kelas
          </Button>
        )}
      </div>

      {classes.length === 0 ? (
        <Card className="border-border shadow-sm">
          <CardContent className="py-14 text-center text-muted-foreground" data-testid="classes-empty">
            Belum ada kelas. Tambahkan kelas pertama Anda.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((c) => (
            <Card key={c.id} className="border-border shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={`class-card-${c.id}`}>
              <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Music className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{c.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{c.instrument}</p>
                  </div>
                </div>
                {canManage && (
                  <div className="flex">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-class-${c.id}`}>
                      <Pencil className="h-4 w-4 text-primary" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-class-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Users className="h-4 w-4" /> {c.student_count} siswa • {c.meetings_per_month || 4}x/bln
                  </span>
                  <span className="font-bold text-primary">{rupiah(c.monthly_fee)}<span className="text-xs font-normal text-muted-foreground">/bln</span></span>
                </div>
                <p className="text-xs text-muted-foreground">Pengajar: {c.teacher_name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="class-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Kelas" : "Tambah Kelas Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Kelas *</Label>
              <Input placeholder="cth: Piano Pemula" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="class-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Instrumen *</Label>
                <Input placeholder="cth: Piano" value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} data-testid="class-instrument-input" />
              </div>
              <div className="space-y-2">
                <Label>SPP / Bulan (Rp)</Label>
                <Input type="number" min="0" placeholder="450000" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} data-testid="class-fee-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Jumlah Pertemuan per Bulan</Label>
              <Input type="number" min="1" max="31" placeholder="4" value={form.meetings_per_month} onChange={(e) => setForm({ ...form, meetings_per_month: e.target.value })} data-testid="class-meetings-input" />
            </div>
            <div className="space-y-2">
              <Label>Pengajar</Label>
              <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                <SelectTrigger data-testid="class-teacher-select"><SelectValue placeholder="Pilih pengajar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belum ditentukan</SelectItem>
                  {teachers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} — {t.specialization || "Umum"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Deskripsi</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="class-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="class-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.instrument} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="class-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
